import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";

import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { appState, useInteractionFlag } from "@fleet/ui";

import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { ShortcutsModal } from "@/components/shortcuts-modal";

import { AppLayoutSidebar } from "./app-layout-sidebar";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";

export function AppLayoutContent() {
  onMount(() => {
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const { t } = useI18n();
  const theme = useTheme();

  const rawLocation = useLocation();
  const routerState = useRouterState();

  const location = rawLocation;

  const routeSectionKey = createMemo(() => location().pathname || "/");
  const isNavigating = () => routerState().status === "pending";

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);

  // Close the mobile sidebar drawer whenever the route changes.
  createEffect(() => {
    void location().pathname;
    setIsSidebarOpen(false);
  });

  // Escape closes the sidebar drawer.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };
    globalThis.addEventListener("keydown", handleEscapeForSidebar);
    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });

  useAppShortcuts({
    location,
    navigate,
    theme,
    t,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). There is no page-level swipe hook in this repo yet, but the flag
  // still needs to be raised where the open/close signal lives, not inside the
  // drawer component: the drawer is tested in isolation and `useInteraction()`
  // throws without an <InteractionProvider> above it.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      background={theme.tokens()?.background}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
      glow0={theme.tokens()?.glow0}
      glow1={theme.tokens()?.glow1}
      glow2={theme.tokens()?.glow2}
    >
      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]">
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <div class="flex flex-1 flex-col min-h-0 h-full">
            {/* Inline guard: a render error in the active route keeps the
                shell (sidebar/header) intact and offers Retry/Reload. */}
            <AppErrorBoundary inline label="route">
              {/* Per-route entrance: keying on the path remounts this wrapper on
                  navigation, replaying `slide-in-up`. `prefers-reduced-motion`
                  is honored globally in global.css. */}
              <Show when={routeSectionKey()} keyed>
                <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                  <Outlet />
                </div>
              </Show>
            </AppErrorBoundary>
          </div>
        </div>
      </main>

      <ShortcutsModal
        isOpen={shortcutsOpen()}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />
    </AppBackground>
  );
}
